/**
 * Composer — rounded pill in the design's walnut palette. Submit is
 * event-handler driven per docs/frontend-react-guidelines.md §4.1.
 *
 * Parents forward a ref so they can measure the composer's bounding box at
 * submit time (the MorphAnimation uses the rect to position the arrow).
 *
 * Auto-resize: the textarea grows with content up to a max height (one
 * sanctioned Effect syncing height with scrollHeight).
 *
 * Experimental hold-to-release (Task 082, ?send=hold): the send control can
 * require a short press-and-hold before it submits. Hold-to-release is UX
 * friction, not abuse protection — server-side controls (Tasks 057/060/058)
 * remain the only boundary. Hold state is Composer-local; submission still
 * happens from the release handler, never an Effect.
 *
 * The composer owns its own draft text (§5.5). Reset is via the parent's
 * `key=` prop, never via prop-to-state mirroring (§4.3).
 */
import { forwardRef, useEffect, useRef, useState } from "react";
import type {
  ChangeEvent,
  CSSProperties,
  FormEvent,
  KeyboardEvent,
  PointerEvent,
} from "react";
import type { Strings } from "../strings";

// Hold-to-release threshold (Task 082). Tunable; keep <= 700ms. The charge-ring
// CSS animation is synced to this via the --hold-ms custom property.
const HOLD_THRESHOLD_MS = 520;

type HoldPhase = "idle" | "charging" | "ready";

export interface ComposerProps {
  strings: Strings;
  disabled: boolean;
  maxLength: number;
  /** Composer position: idle (centered, large) vs docked (bottom of thread). */
  placement: "idle" | "dock";
  /** Override the placeholder when the composer is docked beneath a thread. */
  placeholderOverride?: string;
  /**
   * When set (the docked composer revealed after the landing shot), lights the
   * matching edge once on mount — top edge for "bowstring", a left->right sweep
   * for "aimed-shot" (Task 074). CSS-driven via the data-reveal attribute.
   */
  revealDirection?: "bowstring" | "aimed-shot";
  /**
   * Experimental hold-to-release send mode (Task 082, ?send=hold). When true,
   * the send button requires a short press-and-hold ("draw the bow") before it
   * submits. Default false keeps the normal click/Enter submit unchanged.
   * UX friction, NOT abuse protection.
   */
  holdToRelease?: boolean;
  /**
   * While an answer is in flight (pending or streaming), the send control becomes
   * a visible STOP control that halts generation (handler-driven abort in the
   * parent). Only wired on the docked composer; the textarea stays disabled but
   * the stop button stays clickable.
   */
  busy?: boolean;
  onStop?: () => void;
  /**
   * Task 191: seed the draft once on mount (e.g. a reader "ask about this"
   * prompt starter). Read ONLY at mount — the parent forces a remount via `key`
   * to re-seed (mirrors the §4.3 reset-via-key discipline; no prop-to-state
   * mirroring). The user edits/submits normally from there.
   */
  initialText?: string | undefined;
  onSubmit: (query: string) => void;
}

function StopGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="7" y="7" width="10" height="10" rx="2.5" fill="currentColor" />
    </svg>
  );
}

function SendArrow(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <line
        x1="5"
        y1="12"
        x2="18"
        y2="12"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
      />
      <path
        d="M13 7 L18 12 L13 17"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const Composer = forwardRef<HTMLFormElement, ComposerProps>(function Composer(
  props,
  ref,
): JSX.Element {
  const {
    strings,
    disabled,
    maxLength,
    placement,
    placeholderOverride,
    revealDirection,
    holdToRelease,
    busy,
    onStop,
    initialText,
    onSubmit,
  } = props;
  const [text, setText] = useState<string>(() => initialText ?? "");
  const [focused, setFocused] = useState<boolean>(false);
  const [holdPhase, setHoldPhase] = useState<HoldPhase>("idle");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const holdTimerRef = useRef<number | null>(null);

  // external: HTMLTextAreaElement scrollHeight — auto-resize the textarea
  // up to 200px to fit the user's content. Read-then-write of a layout
  // property; no other state touched.
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta === null) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
  }, [text]);

  // external: window.setTimeout — clear any pending hold-threshold timer on
  // unmount so it cannot fire on a torn-down component (Task 082).
  useEffect(() => {
    return () => {
      if (holdTimerRef.current !== null) {
        window.clearTimeout(holdTimerRef.current);
      }
    };
  }, []);

  // external: window.setTimeout — requirement #7: a disabled/loading composer
  // cancels any in-flight hold (clears the threshold timer + resets the phase).
  useEffect(() => {
    if (!disabled) return;
    if (holdTimerRef.current !== null) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    setHoldPhase("idle");
  }, [disabled]);

  function trySubmit(): void {
    const trimmed = text.trim();
    if (trimmed.length === 0 || disabled) return;
    if (trimmed.length > maxLength) return;
    onSubmit(trimmed);
    setText("");
  }

  function handleFormSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    trySubmit();
  }

  // Textarea Enter-to-send stays instant even in hold mode — the hold ritual
  // applies only to the send button (keyboard speed for the textarea path).
  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      trySubmit();
    }
  }

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>): void {
    setText(event.target.value);
  }

  // --- Hold-to-release handlers (only wired when holdToRelease is true) -------
  function clearHoldTimer(): void {
    if (holdTimerRef.current !== null) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }

  function beginHold(): void {
    if (!holdToRelease || !ready || holdPhase !== "idle") return;
    setHoldPhase("charging");
    // external: window.setTimeout — once the threshold elapses the gesture is
    // releasable ("ready"). Cleared on release/cancel/disable/unmount.
    holdTimerRef.current = window.setTimeout(() => {
      holdTimerRef.current = null;
      setHoldPhase("ready");
    }, HOLD_THRESHOLD_MS);
  }

  function releaseHold(): void {
    if (holdPhase === "idle") return;
    const wasReady = holdPhase === "ready";
    clearHoldTimer();
    setHoldPhase("idle");
    // Submit only past the threshold; early release cancels. Submission stays
    // in the handler, never an Effect (§4.1).
    if (wasReady) {
      trySubmit();
    }
  }

  function cancelHold(): void {
    clearHoldTimer();
    setHoldPhase("idle");
  }

  function handleHoldPointerDown(event: PointerEvent<HTMLButtonElement>): void {
    event.preventDefault(); // avoid text selection / focus quirks on press
    beginHold();
  }

  function handleHoldKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    if (event.key === "Escape") {
      cancelHold();
      return;
    }
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault(); // suppress the default click-on-key behavior
      if (!event.repeat) beginHold();
    }
  }

  function handleHoldKeyUp(event: KeyboardEvent<HTMLButtonElement>): void {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      releaseHold();
    }
  }

  // Render-derived display state. NOT useState mirrors (§5.2).
  const ready = !disabled && text.trim().length > 0 && text.trim().length <= maxLength;
  const placeholder = placeholderOverride ?? strings.composerPlaceholder;
  const className = `composer${focused ? " composer--focus" : ""}`;
  // The placement is purely visual; the form behaves identically in both.
  void placement;

  return (
    <form
      className={className}
      onSubmit={handleFormSubmit}
      aria-label={strings.appTitle}
      data-testid="composer"
      ref={ref}
      data-reveal={revealDirection}
    >
      <div className="composer__inner">
        <textarea
          ref={textareaRef}
          className="composer__textarea"
          placeholder={placeholder}
          value={text}
          maxLength={maxLength}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={disabled}
          rows={1}
          autoFocus
          aria-label={placeholder}
        />
        {busy === true && onStop ? (
          // While an answer is in flight the control halts generation. Stays
          // clickable even though the textarea is disabled (§4.1 handler-driven).
          <button
            type="button"
            className="composer__send composer__send--stop"
            aria-label={strings.composerStopAria}
            onClick={onStop}
            data-testid="composer-stop"
          >
            <StopGlyph />
          </button>
        ) : holdToRelease === true ? (
          <button
            type="button"
            className={`composer__send${ready ? " composer__send--ready" : ""}`}
            data-hold={holdPhase}
            style={
              holdPhase === "charging"
                ? ({ "--hold-ms": `${HOLD_THRESHOLD_MS}ms` } as CSSProperties)
                : undefined
            }
            disabled={!ready}
            aria-label={holdPhase === "idle" ? strings.holdIdle : strings.holdCharging}
            onPointerDown={handleHoldPointerDown}
            onPointerUp={releaseHold}
            onPointerLeave={cancelHold}
            onPointerCancel={cancelHold}
            onKeyDown={handleHoldKeyDown}
            onKeyUp={handleHoldKeyUp}
          >
            <SendArrow />
            {holdPhase !== "idle" ? (
              <svg className="composer__hold-ring" viewBox="0 0 48 48" aria-hidden="true">
                <circle cx="24" cy="24" r="22" />
              </svg>
            ) : null}
          </button>
        ) : (
          <button
            type="submit"
            className={`composer__send${ready ? " composer__send--ready" : ""}`}
            disabled={!ready}
            aria-label={strings.composerSubmit}
          >
            <SendArrow />
          </button>
        )}
      </div>
      <div className="composer__footer">
        <span className="composer__hint">{strings.composerHintTagline}</span>
        <span>
          <kbd>↵</kbd> {strings.composerHintEnter}
        </span>
        <span className="composer__char-count" aria-live="off">
          {strings.composerCharCount(text.length, maxLength)}
        </span>
      </div>
    </form>
  );
});
